import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Search, Store, User, LogOut, LayoutDashboard, Package, Radio, Factory, Globe, ChevronDown, ShoppingCart, Coins } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useSiteConfig } from "../context/ConfigContext";
import { LANGS } from "../lib/i18n";
import { BrandS, BrandWordmark } from "./Brand";

export const BrandMark = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 select-none">
    <span className="h-9 w-9"><BrandS /></span>
    <BrandWordmark />
  </Link>
);

export const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="nav-logo-simple" className="flex items-center gap-2 select-none">
    <span className="h-8 w-8"><BrandS /></span>
    <span className={`font-heading text-xl font-extrabold tracking-tight ${dark ? "text-white" : "text-teal-950"}`}>
      MARHINE<span className="text-teal-500">.</span>
    </span>
  </Link>
);

export function LangMenu({ mobile = false }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid={mobile ? "lang-switcher-mobile" : "lang-switcher"}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-800 transition"
        aria-label="Language"
      >
        <Globe className="h-4 w-4" /> {lang.toUpperCase()} <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div data-testid="lang-menu" className="absolute right-0 z-50 mt-1 w-36 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => {
                setOpen(false);
                setLang(l.code);
              }}
              className={`block w-full px-4 py-2.5 text-left text-sm font-medium transition hover:bg-teal-50 ${
                l.code === lang ? "text-teal-700 bg-teal-50/60" : "text-slate-700"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CurrencyMenu({ mobile = false }) {
  const { code, setCode, currencies } = useCurrency();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid={mobile ? "currency-switcher-mobile" : "currency-switcher"}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-800 transition"
        aria-label="Currency"
      >
        <Coins className="h-4 w-4" /> {code} <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div data-testid="currency-menu" className="absolute right-0 z-50 mt-1 max-h-72 w-36 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
          {currencies.map((c) => (
            <button
              key={c.code}
              data-testid={`currency-option-${c.code}`}
              onClick={() => {
                setOpen(false);
                setCode(c.code);
              }}
              className={`block w-full px-4 py-2.5 text-left text-sm font-medium transition hover:bg-teal-50 ${
                c.code === code ? "text-teal-700 bg-teal-50/60" : "text-slate-700"
              }`}
            >
              {c.symbol} {c.code}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout, isSeller } = useAuth();
  const { t } = useLang();
  const { features, auction } = useSiteConfig();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    setOpen(false);
    navigate(q.trim() ? `/products?q=${encodeURIComponent(q.trim())}` : "/products");
  };

  const onLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  // My Simashi pages have their own in-page headers — no site navbar
  if (pathname.startsWith("/account")) return null;

  const links = [
    { to: "/products", label: t("nav_products"), tid: "nav-link-browse", match: (p) => p.startsWith("/product") },
    { to: "/suppliers", label: t("nav_suppliers"), tid: "nav-link-suppliers", match: (p) => p.startsWith("/suppliers") || p.startsWith("/store") },
    ...(features.live !== false
      ? [{ to: "/live", label: t("nav_live"), tid: "nav-link-live", match: (p) => p.startsWith("/live") }]
      : []),
    ...(auction?.enabled
      ? [{ to: "/auctions", label: t("nav_auctions"), tid: "nav-link-auctions", match: (p) => p.startsWith("/auctions") }]
      : []),
    { to: "/#worldwide", label: t("quick_worldwide"), tid: "nav-link-worldwide", match: () => false },
  ];

  const tabCls = (active) =>
    isHome
      ? `relative py-1.5 transition-colors ${active ? "font-extrabold text-slate-900" : "font-semibold text-slate-500 hover:text-slate-900"}`
      : `relative py-1.5 transition-colors hover:text-teal-800 ${active ? "font-extrabold text-slate-900" : "font-semibold text-slate-600"}`;

  const underline = (active) =>
    active ? (
      <span
        className="absolute -bottom-0.5 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full"
        style={{ backgroundColor: isHome ? "var(--ap, #EA580C)" : "var(--ap, #ea580c)" }}
      />
    ) : null;

  const isHome = pathname === "/";

  return (
    <header className={`sticky top-0 z-50 ${isHome ? "bg-white" : "border-b border-slate-200 bg-white/95 backdrop-blur-md"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="hidden md:flex h-14 items-center gap-6">
          <BrandMark />
          <nav className="flex items-center gap-7 ml-6 text-[15px]">
            {links.map((l) => (
              <Link key={l.tid} data-testid={l.tid} to={l.to} className={tabCls(l.match(pathname))}>
                {l.label}
                {underline(l.match(pathname))}
              </Link>
            ))}
          </nav>
        </div>
        {!pathname.startsWith("/account") && (
        <nav
          data-testid="nav-tabs-row"
          className={`${isHome ? "flex" : "md:hidden flex"} items-center gap-6 overflow-x-auto text-sm py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
        >
          {links.map((l) => (
            <Link key={l.tid} data-testid={`${l.tid}-m`} to={l.to} className={`shrink-0 ${tabCls(l.match(pathname))}`}>
              {l.label}
              {underline(l.match(pathname))}
            </Link>
          ))}
        </nav>
        )}
      </div>
      {open && (
        <div data-testid="nav-mobile-drawer" className="md:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3">
          <form onSubmit={submitSearch} role="search">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="nav-mobile-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("nav_search_ph")}
                className="w-full rounded-full border border-slate-200 bg-slate-50 pl-9 pr-4 py-2 text-sm outline-none focus:border-teal-500"
              />
            </div>
          </form>
          <div className="flex items-center gap-1">
            <LangMenu mobile />
            <CurrencyMenu mobile />
          </div>
          <Link data-testid="nav-mobile-browse" to="/products" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium text-slate-800">
            <Package className="h-4 w-4 text-teal-600" /> {t("nav_products")}
          </Link>
          <Link data-testid="nav-mobile-suppliers" to="/suppliers" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium text-slate-800">
            <Factory className="h-4 w-4 text-teal-600" /> {t("nav_suppliers")}
          </Link>
          {features.live !== false && (
            <Link data-testid="nav-mobile-live" to="/live" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium text-slate-800">
              <Radio className="h-4 w-4 text-red-500" /> {t("live_t")}
            </Link>
          )}
          {features.cart !== false && (
            <Link data-testid="nav-mobile-cart" to="/cart" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium text-slate-800">
              <ShoppingCart className="h-4 w-4 text-teal-600" /> {t("nav_cart")}
            </Link>
          )}
          <Link data-testid="nav-mobile-sell" to="/seller" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium text-slate-800">
            <Store className="h-4 w-4 text-teal-600" /> {t("nav_sell")}
          </Link>
          {user ? (
            <>
              {isSeller && (
                <Link data-testid="nav-dashboard-link" to="/seller" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium text-slate-800">
                  <LayoutDashboard className="h-4 w-4 text-teal-600" /> {t("nav_dashboard")}
                </Link>
              )}
              <Link data-testid="nav-mobile-account" to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium text-slate-800">
                <User className="h-4 w-4 text-teal-600" /> {t("nav_account")}
              </Link>
              <button data-testid="nav-mobile-logout" onClick={onLogout} className="flex items-center gap-2 py-2 text-sm font-medium text-red-600">
                <LogOut className="h-4 w-4" /> {t("nav_logout")}
              </button>
            </>
          ) : (
            <div className="flex gap-2 pt-2">
              <Link data-testid="nav-mobile-login" to="/login" onClick={() => setOpen(false)} className="flex-1 rounded-full border border-teal-700 px-4 py-2 text-center text-sm font-bold text-teal-700">
                {t("nav_login")}
              </Link>
              <Link data-testid="nav-mobile-register" to="/register" onClick={() => setOpen(false)} className="flex-1 rounded-full bg-teal-700 px-4 py-2 text-center text-sm font-bold text-white">
                {t("nav_join")}
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
